import { Component, ViewChild, computed, effect, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AgentChatService } from './services/agent-chat.service';
import { ChatAttachment, ChatMessage } from '../../core/models/chat-message';
import { MessageComposerComponent } from '../../components/message-composer/message-composer';
import { MessageListComponent } from '../../components/message-list/message-list';
import { OptionPickerComponent, OptionPickerOption } from '../../components/option-picker/option-picker';
import { ChatPersistenceService } from '../../core/services/chat-persistence.service';
import { LmStudioService } from '../../core/services/lm-studio.service';
import { ThinkingMode } from '../../core/models/thinking-mode';
import { BillSplitterService } from '../../features/bill-splitter/bill-splitter.service';
import { SettingsSyncService } from '../../core/services/settings-sync.service';

type MenuAction = 'settings' | 'delete';
const CHAT_EXPANDED_STORAGE_KEY = 'serg-chat-expanded-v1';

function userMessageCount(messages: ChatMessage[]): number {
  return messages.reduce((count, message) => (message.author === 'user' ? count + 1 : count), 0);
}

@Component({
  selector: 'app-chat-page',
  imports: [MessageComposerComponent, MessageListComponent, OptionPickerComponent],
  templateUrl: './chat-page.html',
  styleUrl: './chat-page.scss',
})
export class ChatPageComponent {
  private readonly agentChat = inject(AgentChatService);
  private readonly llm = inject(LmStudioService);
  private readonly chatPersistence = inject(ChatPersistenceService);
  private readonly settingsSync = inject(SettingsSyncService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly billSplitter = inject(BillSplitterService);
  @ViewChild(MessageListComponent) private messageList?: MessageListComponent;
  private readonly pendingThreadIds = signal<ReadonlySet<string>>(new Set());
  private readonly abortControllers = new Map<string, AbortController>();
  private lastProcessedKeyword?: string;
  private pendingAttachments: ChatAttachment[] = [];

  protected readonly draft = signal('');
  protected readonly model = signal(this.llm.mainModelId());
  protected readonly modelOptions = signal<ReadonlyArray<OptionPickerOption<string>>>([
    { value: this.model(), label: this.model() },
  ]);
  protected readonly thinkingMode = signal<ThinkingMode>('auto');
  protected readonly isExpanded = signal(this.readExpandedState());
  protected readonly messages = this.chatPersistence.activeMessages;
  protected readonly chatTitle = computed(() => this.chatPersistence.activeThread()?.title ?? 'New chat');
  protected readonly topicThreadId = computed(() => this.chatPersistence.activeThreadId() ?? '');
  protected readonly cachedTopics = computed(() => this.chatPersistence.activeThread()?.relatedTopics);
  protected readonly isThinking = computed(() => {
    const threadId = this.chatPersistence.activeThreadId();
    return threadId ? this.pendingThreadIds().has(threadId) : false;
  });
  protected readonly isRetrieving = this.isThinking;
  protected readonly retrievalStatus = this.agentChat.status;

  protected readonly menuTriggerIcon =
    '<svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor" stroke="none"><circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" /></svg>';

  protected readonly menuOptions: ReadonlyArray<OptionPickerOption<MenuAction>> = [
    {
      value: 'settings',
      label: 'Settings',
      icon: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>',
    },
    {
      value: 'delete',
      label: 'Delete chat',
      danger: true,
      icon: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 6h18" /><path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /></svg>',
    },
  ];

  constructor() {
    void this.loadModels();
    this.route.paramMap.subscribe((params) => this.openThread(params.get('threadId')));

    this.route.queryParamMap.subscribe((params) => {
      const keyword = params.get('q') ?? undefined;
      if (!keyword || keyword === this.lastProcessedKeyword) return;
      this.lastProcessedKeyword = keyword;
      this.draft.set(keyword);
      setTimeout(() => void this.sendMessage());
    });

    // Generate a durable title after each completed user/assistant exchange.
    // The thread's titledKey (persisted in storage) records which conversation
    // state the current title was generated for, so reopening the page does
    // not trigger another title-generation LLM call.
    effect(() => {
      const thread = this.chatPersistence.activeThread();
      const messages = this.messages();
      const latestUserIndex = messages.map((message) => message.author).lastIndexOf('user');
      const hasReply = latestUserIndex >= 0 && messages.slice(latestUserIndex + 1).some((message) => message.author === 'bot');
      const conversationKey = `${thread?.id ?? ''}:${latestUserIndex}:${messages.length}`;
      const needsTitle = !thread?.titledKey || (thread.title === 'New chat' && userMessageCount(messages) > 0);
      if (thread && hasReply && !this.isThinking() && (needsTitle || thread.titledKey !== conversationKey)) {
        void this.updateChatTitle(thread.id, messages, conversationKey);
      }
    });

  }

  protected onComposerAttachments(attachments: ChatAttachment[]): void {
    this.pendingAttachments = attachments;
  }

  protected onDraftChange(value: string): void {
    this.draft.set(value);
    this.messageList?.scrollToBottom();
  }

  protected onModelChange(model: string): void {
    this.model.set(model);
    void this.settingsSync.save({ ...this.chatPersistence.getEffectiveSettings(), model });
  }

  protected toggleChatSize(): void {
    this.isExpanded.update((expanded) => {
      const next = !expanded;
      this.persistExpandedState(next);
      return next;
    });
  }

  protected async sendMessage(): Promise<void> {
    const text = this.draft().trim();
    const attachments = this.pendingAttachments;
    this.pendingAttachments = [];
    if ((!text && attachments.length === 0) || this.isThinking()) return;

    const thread = this.chatPersistence.openThread(this.chatPersistence.activeThreadId());
    const threadId = thread.id;
    const userMessage: ChatMessage = {
      author: 'user',
      text,
      time: this.currentTime(),
      attachments: attachments.length ? attachments : undefined,
    };
    this.chatPersistence.clearRelatedTopics(threadId);
    const updatedMessages = [...this.messages(), userMessage];
    this.chatPersistence.setMessages(threadId, updatedMessages);
    if (!thread.messages.some((message) => message.author === 'user')) {
      this.chatPersistence.updateTitle(threadId, this.localTitle(text || attachments.map((attachment) => attachment.name).join(', ')));
    }
    this.draft.set('');
    this.setThreadPending(threadId, true);
    const controller = new AbortController();
    this.abortControllers.set(threadId, controller);

    try {
      this.agentChat.status.set('Choosing the most useful response…');
      const tool = await this.agentChat.decideInteractiveTool(updatedMessages);
      if (tool.tool === 'open_bill_splitter') {
        const bill = this.billSplitter.createBill();
        for (const name of tool.people) this.billSplitter.applyOperation(bill.id, { type: 'add_person', name });
        const sourceAttachment = attachments.find((attachment) => attachment.dataUrl || attachment.text);
        this.chatPersistence.addMessage(threadId, { author: 'bot', text: 'I opened an interactive Bill Splitter. Review the items and adjust any allocation below.', time: this.currentTime(), toolInvocation: { name: 'open_bill_splitter', billId: bill.id, sourceText: sourceAttachment ? '' : text, sourceAttachment, instructions: tool.instructions } });
        return;
      }
      if (tool.tool === 'open_bill_viewer') {
        this.chatPersistence.addMessage(threadId, { author: 'bot', text: 'Here are your saved bills.', time: this.currentTime(), toolInvocation: { name: 'open_bill_viewer' } });
        return;
      }
      const assistantMessage: ChatMessage = { author: 'bot', text: '', time: this.currentTime() };
      this.chatPersistence.addMessage(threadId, assistantMessage);
      const response = await this.agentChat.stream(updatedMessages, { thinkingMode: this.thinkingMode(), model: this.model() }, (update) => {
        this.chatPersistence.updateLastMessage(threadId, { ...assistantMessage, ...update }, false);
      }, controller.signal);
      this.chatPersistence.updateLastMessage(threadId, { ...assistantMessage, ...response });
    } catch (error) {
      if (controller.signal.aborted) {
        // The user stopped generation — keep any partial text that already streamed in.
        const last = this.chatPersistence.activeMessages().at(-1);
        if (!last?.text) {
          this.chatPersistence.updateLastMessage(threadId, {
            author: 'bot',
            text: 'Stopped.',
            time: this.currentTime(),
          });
        }
      } else {
        this.chatPersistence.updateLastMessage(threadId, {
          author: 'bot',
          text: 'I could not reach LM Studio. Make sure it is running at 127.0.0.1:1234 and try again.',
          time: this.currentTime(),
        });
      }
    } finally {
      this.abortControllers.delete(threadId);
      this.setThreadPending(threadId, false);
    }
  }

  protected stopChat(): void {
    const threadId = this.chatPersistence.activeThreadId();
    if (threadId) this.abortControllers.get(threadId)?.abort();
  }

  protected askRelatedTopic(topic: string): void {
    this.draft.set(topic);
    void this.sendMessage();
  }

  protected onMenuSelect(action: MenuAction): void {
    if (action === 'settings') {
      void this.router.navigate(['/settings']);
      return;
    }
    if (action === 'delete') {
      const threadId = this.chatPersistence.activeThreadId();
      if (!threadId) return;
      if (window.confirm('Delete this conversation? This cannot be undone.')) {
        this.abortControllers.get(threadId)?.abort();
        this.chatPersistence.deleteThread(threadId);
        void this.router.navigate(['/chat']);
      }
    }
  }

  private openThread(threadId: string | null): void {
    const thread = this.chatPersistence.openThread(threadId);
    if (threadId !== thread.id) {
      void this.router.navigate(['/chat', thread.id], {
        replaceUrl: true,
        queryParams: this.route.snapshot.queryParams,
      });
    }
  }

  private async loadModels(): Promise<void> {
    try {
      const models = await this.llm.listModels();
      const selected = this.model();
      const available = models.includes(selected) ? models : [selected, ...models];
      this.modelOptions.set(available.map((model) => ({ value: model, label: model })));
    } catch {
      // Keep the configured model selectable when its endpoint is offline.
    }
  }

  private readExpandedState(): boolean {
    try {
      return localStorage.getItem(CHAT_EXPANDED_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  }

  private persistExpandedState(expanded: boolean): void {
    try {
      localStorage.setItem(CHAT_EXPANDED_STORAGE_KEY, String(expanded));
    } catch {
      // Ignore storage failures in strict/private browsing modes.
    }
  }

  private setThreadPending(threadId: string, pending: boolean): void {
    this.pendingThreadIds.update((threadIds) => {
      const next = new Set(threadIds);
      pending ? next.add(threadId) : next.delete(threadId);
      return next;
    });
  }

  private async updateChatTitle(threadId: string, messages: ChatMessage[], titledKey: string): Promise<void> {
    const userMessages = messages.filter((message) => message.author === 'user');
    const recentMessages = messages.slice(-6);
    const titlePrompt = `Based on this conversation history, generate a short, descriptive title (max 5 words) that summarizes the main topic of discussion. Respond with only the title text:\n\n${recentMessages.map((message) => `${message.author}: ${message.text}`).join('\n')}`;

    try {
      // Keep title generation separate from the RAG response flow. It is a
      // lightweight task, so route it to the helper model when one is configured.
      const titleResponse = await this.llm.chat([{ author: 'user', text: titlePrompt, time: this.currentTime() }], { model: this.llm.helperModelId(), timeoutMs: 8_000, useHelperApi: true });
      this.chatPersistence.updateTitle(threadId, this.cleanTitle(titleResponse, userMessages.at(-1)?.text), titledKey);
    } catch {
      this.chatPersistence.updateTitle(threadId, this.localTitle(userMessages.at(-1)?.text), titledKey);
    }
  }

  private cleanTitle(title: string, fallback?: string): string {
    const cleanTitle = title.trim().replace(/^['"]|['"]$/g, '');
    return !cleanTitle || /\b(?:i cannot|cannot|not provide)\b/i.test(cleanTitle)
      ? this.localTitle(fallback)
      : cleanTitle.slice(0, 80);
  }

  private localTitle(text?: string): string {
    const words = text?.trim().split(/\s+/).filter(Boolean).slice(0, 5) ?? [];
    return words.length ? words.join(' ') : 'New chat';
  }

  private currentTime(): string {
    return new Intl.DateTimeFormat('en', { hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date());
  }
}
